import { useEffect, useRef, useState } from 'react';

export function useRefState<T>(initialValue: T) {

  const [state, setState] = useState(initialValue);
  const ref = useRef(state);

  const setReferredState = (value: T) => {

    if (typeof value === 'function') {

      let oldValue;
      setState(oldVal => {
        oldValue = oldVal;
        return value(oldVal)
      });
      ref.current = value(oldValue);

    } else {
      setState(value);
      ref.current = value;
    }
  };

  return [state, ref, setReferredState];
}

export function useOutsideClick(node: HTMLElement | undefined | null, callback: (evt: MouseEvent) => void, ignoredTargetOnOutsideClick?: HTMLElement | undefined | null) {

  useEffect(() => {

    function handleOutsideClick(evt: MouseEvent) {

      if (node &&
        !node.contains(evt.target as Node) &&
        !ignoredTargetOnOutsideClick?.contains(evt.target as Node) &&
        // isConnected check needed because some click handlers unmount the clicked element (e.g. speaker input group buttons)
        (evt.target as Node)?.isConnected &&
        typeof callback === 'function') {

        callback(evt);

      }
    }

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [node, callback, ignoredTargetOnOutsideClick]);
}

export function useOutsideScroll(node: HTMLElement | undefined | null, callback: (evt: Event) => void) {

  useEffect(() => {

    function handleOutsideScroll(evt: Event) {

      if (node &&
        !node.contains(evt.target as Node) &&
        // isConnected check needed because some click handlers unmount the clicked element (e.g. speaker input group buttons)
        (evt.target as Node)?.isConnected &&
        typeof callback === 'function') {

        callback(evt);

      }
    }

    document.addEventListener('wheel', handleOutsideScroll);
    return () => {
      document.removeEventListener('wheel', handleOutsideScroll);
    };
  }, [node, callback]);
}

// mousedown seems to work better than click for catching outside clicks in certain scenarios (i.e. stopPropagation)
export function useOutsideMouseDown(node: HTMLElement | HTMLElement[] | undefined | null, callback: (evt: MouseEvent) => void) {

  useEffect(() => {

    function handleOutsideClick(evt: MouseEvent) {
      if (node &&
        Array.isArray(node) &&
        node[0]) {
        const filteredNodes: HTMLElement[] = node.filter((element: HTMLElement) => element.contains(evt.target as Node));
        if (filteredNodes.length === 0 &&
          (evt.target as Node)?.isConnected &&
          typeof callback === 'function') {
          callback(evt);
        }
      }

      if (node &&
        !Array.isArray(node) &&
        !node.contains(evt.target as Node) &&
        // isConnected check needed because some click handlers unmount the clicked element (e.g. speaker input group buttons)
        (evt.target as Node)?.isConnected &&
        typeof callback === 'function') {

        callback(evt);

      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [node, callback]);
}

export function useWindowResize() {
  const [width, setWidth] = useState<number>();
  const [height, setHeight] = useState<number>();

  useEffect(() => {
    function handleWindowResize() {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    }

    window.addEventListener('resize', handleWindowResize);

    handleWindowResize();

    return () => window.removeEventListener('resize', handleWindowResize);

  }, [])

  return [width, height];
}

/**
 * Hook to delay unmounting of a component to allow exit animations.
 * Returns { shouldRender, isAnimating } where:
 * - shouldRender: true when component should be in the DOM
 * - isAnimating: 'entering' | 'exiting' | null for animation state
 */
export function useDelayedUnmount(isVisible: boolean, exitDelayMs: number = 200) {
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [isAnimating, setIsAnimating] = useState<'entering' | 'exiting' | null>(
    isVisible ? 'entering' : null
  );

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setIsAnimating('entering');
      // Clear entering state after animation completes
      const timer = setTimeout(() => setIsAnimating(null), 50);
      return () => clearTimeout(timer);
    } else if (shouldRender) {
      setIsAnimating('exiting');
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsAnimating(null);
      }, exitDelayMs);
      return () => clearTimeout(timer);
    }
  }, [isVisible, exitDelayMs, shouldRender]);

  return { shouldRender, isAnimating };
}
/**
 * Dialog behaviour for a hand-rolled modal: Escape closes it, focus moves inside on open and
 * returns to whatever opened it on close, and Tab is contained so it cannot wander the page
 * behind the overlay.
 *
 * ponytail: no inert/`<dialog>` polyfill — the page behind stays in the accessibility tree for
 * screen-reader virtual cursors. Swap to the native element once the supported browser floor allows.
 */
export function useDialogBehavior(onClose: () => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Callers pass an inline arrow, so onClose is a new function every render. Depending on it would
  // tear down and re-run this effect on each keystroke, moving focus back to the first control and
  // making the form impossible to type in. The ref keeps the latest callback without re-running.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const focusables = () => Array.from(
      ref.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    );

    focusables()[0]?.focus();

    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        evt.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (evt.key !== 'Tab')
        return;

      const items = focusables();
      if (!items.length)
        return;

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (evt.shiftKey && active === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && active === last) {
        evt.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus?.();
    };
    // Mount only: the dialog's identity does not change while it is open.
  }, []);

  return ref;
}

/** Match the existing narrow-layout breakpoint, including browser resizing. */
export function useNarrowLayout() {
  const [narrow, setNarrow] = useState(() => window.matchMedia?.('(max-width: 900px)').matches ?? false);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 900px)');
    const update = () => setNarrow(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return narrow;
}
